<template lang="pug">
#other-user-group-type-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者グループ種類情報修正

    .box-body
      .other-user-group-type-table-component
        // テーブル
        b-table(
          :items="otherUserGroupTypeItems"
          :fields="otherUserGroupTypeFields"
          :busy="isLoading"
          sticky-header
          no-border-collapse
          table-class="basic-table"
          show-empty
        )
          // ローディング
          template(#table-busy)
            .text-center.table-loading-message
              b-spinner.spinner
              span.message.align-self-center 読込み中...

          // セル：種類名
          template(#cell(name)="row")
            div(v-if="row.item.id == 1") {{ row.item.name }}
            input.register-form(
              v-else
              v-model="row.item.name"
              :name="'group-type-name-' + row.item.id"
              type="text"
              placeholder="種類名"
              v-validate="{ required: true, max: 32 }"
              maxlength="32"
              data-vv-as="種類名"
              :class="{ noinput: (!row.item.name) }")
            .error-messages(v-for="error in errors.collect('group-type-name-' + row.item.id)")
              label.error-message {{ error }}
              br
          // セル：説明
          template(#cell(description)="row")
            input.register-form(
              v-model="row.item.description"
              :name="'group-type-description-' + row.item.id"
              type="text"
              placeholder="説明"
              v-validate="{ max: 32 }"
              maxlength="32"
              data-vv-as="説明")
            .error-messages(v-for="error in errors.collect('group-type-description-' + row.item.id)")
              label.error-message {{ error }}
              br

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoOtherUserGroupTypeList()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="施設関係者グループ種類情報を更新しますか？" @ok="updateOtherUserGroupType")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoOtherUserGroupTypeList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { OtherUserGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import OtherUserGroupTypesService, { OtherUserGroupTypesPatchModel, OtherUserGroupTypesItem } from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";

/**
 * 施設関係者グループ一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherUserGroupTypeEdit extends Vue {
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 施設関係者グループ種類の列 */
  otherUserGroupTypeFields: Array<OtherUserGroupTypeTableField> = [];
  /** 施設関係者グループ種類の行 */
  otherUserGroupTypeItems: Array<OtherUserGroupTypeTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    const otherUserGroupTypeFields: Array<OtherUserGroupTypeTableField> = this.createOtherUserGroupTypeFields();
    this.otherUserGroupTypeFields = otherUserGroupTypeFields;

    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeItems: Array<OtherUserGroupTypeTableItem> = this.createOtherUserGroupTypeItems(otherUserGroupTypes);
    this.otherUserGroupTypeItems = otherUserGroupTypeItems;

    this.isLoading = false;

    return;
  }

  /**
   * ### 列名の作成
   */
  public createOtherUserGroupTypeFields(): Array<OtherUserGroupTypeTableField> {
    let fields: Array<OtherUserGroupTypeTableField> = [];

    fields.push({ key: "id", label: "番号", sortable: false, sortDirection: "desc", class: "id" });
    fields.push({ key: "name", label: "種類名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "description", label: "説明", sortable: false, sortDirection: "desc", class: "description" });

    return fields;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 行の作成
   */
  public createOtherUserGroupTypeItems(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Array<OtherUserGroupTypeTableItem> {
    let items: Array<OtherUserGroupTypeTableItem> = [];

    for (const type of otherUserGroupTypes) {
      items.push({
        id: type.id,
        name: type.name,
        description: type.description,
        updated: type.updated
      });
    }

    return items;
  }

  /**
   * ### 更新確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 施設関係者グループ更新
   */
  public async updateOtherUserGroupType(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const otherUserGroupTypes: Array<OtherUserGroupTypesItem> = [];
      for (const type of this.otherUserGroupTypeItems) {
        otherUserGroupTypes.push({
          id: type.id,
          name: type.name,
          description: type.description
        });
      }
      const requestModel: OtherUserGroupTypesPatchModel = {
        otherUserGroupTypes: otherUserGroupTypes,
        updated: this.otherUserGroupTypeItems[0].updated
      };
      await this.otherUserGroupTypesService.patch(userStore, requestModel);

      // 施設関係者を再読み込みさせるようstoreを更新
      let otherUserGroupList: OtherUserGroupListState = needRefreshesStore.otherUserGroupList;
      otherUserGroupList.otherUserGroupList = true;
      needRefreshesStore.setOtherUserGroupList(otherUserGroupList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設関係者グループ種類編集画面へ遷移
   */
  public gotoOtherUserGroupTypeList(): void {
    this.$router.push(PcUrl.OtherUserGroupTypeList);

    return;
  }
}

/** テーブルの列名の情報 */
export interface OtherUserGroupTypeTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface OtherUserGroupTypeTableItem {
  id: number;
  name: string;
  description: string;
  updated: number;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#other-user-group-type-edit {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .other-user-group-type-table-component {
    .register-form {
      width: 100%;
      display: block;
      height: 42px;
      padding: 6px 12px;
      font-size: 16px;
      background-image: none;
      background-color: $bg-default;
      border: 1px solid $form-border-color;
      border-radius: 0.25rem;
    }
    .noinput {
      background-color: $bg-require-input;
    }
    // 入力欄にフォーカスが当たった際は強調する
    input:focus {
      // chromeが自動的に付与するスタイルを無効化する
      outline: none;
      box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
      border: 1px solid $form-border-select-color !important;
      border-radius: 0.25rem;
    }
  }
}
</style>
